// How a carrier's payment report is grouped for review.
//
// The reconcile stages one row per line of the report, each carrying what it
// would do (`proposedAction`) and why it could not just do it (`reason`). A
// group is a view over those two, not a field of its own.
//
// Only the three names live here now. Grouping rows in the browser was the old
// way of counting them: the screen fetched a page and tallied it, which
// undercounted any group larger than the page and disagreed with the same run's
// figures on the Carriers card. The server counts them over the whole run and
// returns the counts, so the rule that decides a group belongs there — see
// `ReconcileGroup` in checkme-backend — and nothing here needs to repeat it.

/** To mark paid: the carrier has the receipt and we do not. */
export const MARK_PAID = 'MARK_PAID';
/** The carrier stopped collecting — accepting deactivates the policy. */
export const CANCEL_POLICY = 'CANCEL_POLICY';
/** Wider than its name: every line that could not be acted on and was not already settled. */
export const NO_MATCH = 'NO_MATCH';
/**
 * AXA: a policy's schedule rebuilt from the carrier's receipts. Applied by the
 * backend as it reads -- except for a policy whose listing had a row that could
 * not be read, or whose automatic update failed. Those stay PENDING here and are
 * decided by a person (CarrierHeldSchedules).
 */
export const SYNC_SCHEDULE = 'SYNC_SCHEDULE';

/**
 * How wide a drawer opens while a payments review is in it: the review's own width on its own
 * (PaymentsReviewModal `reviewWidth`), so embedding it does not cut off the columns it was laid
 * out to show side by side.
 */
export const PAYMENTS_REVIEW_WIDTH = 'min(1760px, calc(100vw - 32px))';

/**
 * A schedule proposal's months, side by side: the instalment Renova holds and
 * the carrier's receipt for each due date in the span, oldest first. `changed`
 * when either side is missing or they differ in amount, status, or -- for a paid
 * one -- the day it was paid.
 *
 * A day can hold more than one instalment (Skandia: a rejected charge and the
 * retry that went through), so lines are paired within a day by status first,
 * then in order, never merged; `key` is unique per line for v-for.
 *
 * @param detail a SYNC_SCHEDULE row's `detail` (its `current` and `target`)
 */
export function scheduleLines(detail) {
  const byDay = (list) => {
    const days = new Map();
    (list || []).forEach((p) => {
      if (!days.has(p.dueDate)) days.set(p.dueDate, []);
      days.get(p.dueDate).push(p);
    });
    return days;
  };
  const current = byDay(detail && detail.current);
  const target = byDay(detail && detail.target);
  const differs = (c, t) => !c || !t
    || Number(c.amount) !== Number(t.amount)
    || c.status !== t.status
    || (t.status === 'PAID' && c.paidDate !== t.paidDate);
  const days = [...new Set([...current.keys(), ...target.keys()])].sort((a, b) => a.localeCompare(b));
  const lines = [];
  days.forEach((dueDate) => {
    const left = [...(current.get(dueDate) || [])];
    const right = [...(target.get(dueDate) || [])];
    const pairs = [];
    // Same status first, so a PAID meets the PAID receipt and a CANCELED the rejected one.
    right.slice().forEach((t) => {
      const i = left.findIndex((c) => c.status === t.status);
      if (i >= 0) {
        pairs.push([left.splice(i, 1)[0], t]);
        right.splice(right.indexOf(t), 1);
      }
    });
    // Whatever is left pairs up in order; the longer side's rest stands alone.
    while (left.length || right.length) pairs.push([left.shift() || null, right.shift() || null]);
    pairs.forEach(([c, t], i) => lines.push({
      key: `${dueDate}#${i}`, dueDate, current: c, target: t, changed: differs(c, t),
    }));
  });
  return lines;
}

/**
 * Why a SYNC_SCHEDULE row was held for a person rather than applied: one entry
 * per payment Renova has as paid or cancelled that the carrier disagrees with,
 * or lists nothing for (`{ kind: 'keptInRenova', dueDate, status, receipt }`,
 * `receipt` null when the carrier lists none), per carrier row that
 * could not be read (`{ kind: 'unreadable', row, reason }`), or the automatic
 * update's error (`{ kind: 'applyError', message }`).
 */
export function heldReasons(detail) {
  const d = detail || {};
  const kept = (Array.isArray(d.keptInRenova) ? d.keptInRenova : [])
    .filter((p) => p && p.dueDate)
    .map((p) => ({
      kind: 'keptInRenova', dueDate: p.dueDate, status: p.status || 'PAID', receipt: p.receipt || null,
    }));
  const unreadable = (Array.isArray(d.unreadableRows) ? d.unreadableRows : [])
    .map((r) => ({ kind: 'unreadable', row: r && r.sourceRow, reason: (r && r.reason) || '' }));
  const reasons = kept.concat(unreadable);
  if (d.applyError) reasons.push({ kind: 'applyError', message: String(d.applyError) });
  return reasons;
}

/**
 * What accepting a held row would do, from its `detail.changes`: the non-zero
 * pieces, in reading order, each `{ key, count, label }` with `label` the same
 * words a payments run's own line uses ("payments marked paid").
 */
export function heldChanges(detail) {
  const c = (detail && detail.changes) || {};
  return [
    ['paid', 'carriers.import.paymentsLine.paid'],
    ['canceled', 'carriers.import.paymentsLine.canceled'],
    ['added', 'carriers.import.paymentsLine.added'],
    ['removed', 'carriers.import.paymentsLine.removed'],
  ]
    .map(([key, label]) => ({ key, count: Number(c[key]) || 0, label }))
    .filter((piece) => piece.count > 0);
}

/**
 * What a payments read changed on one policy, as `heldChanges` pieces.
 *
 * A held row carries its own `changes`. A row the read applied by itself does
 * not -- the backend records them only for a decision -- so they are counted
 * here from its `current` and `target`, by the backend's own rule
 * (`AxaPaymentReconcileService.changesOf`): by due date, a PAID or CANCELED in
 * the carrier's listing that Renova did not have as such, a date only the
 * carrier has (added), a date only Renova had (removed).
 */
export function rowChanges(detail) {
  const d = detail || {};
  if (d.changes && typeof d.changes === 'object') return heldChanges(d);
  if (!Array.isArray(d.target)) return [];
  const statuses = (list) => new Map((Array.isArray(list) ? list : [])
    .filter((p) => p && p.dueDate)
    .map((p) => [p.dueDate, p.status]));
  const was = statuses(d.current);
  const now = statuses(d.target);
  const count = (status) => [...now].filter(([day, s]) => s === status && was.get(day) !== status).length;
  return heldChanges({
    changes: {
      paid: count('PAID'),
      canceled: count('CANCELED'),
      added: [...now.keys()].filter((day) => !was.has(day)).length,
      removed: [...was.keys()].filter((day) => !now.has(day)).length,
    },
  });
}

/**
 * The filters of a payments read's full policy list, as the list endpoint's own
 * parameters -- every one is applied by the server, so each count is a
 * `totalCount` and agrees with the run's line ("32 policies read · 29 not found
 * in Renova"). There is no reason-level filter on the server: "Up to date" is
 * every matched row the read left alone, and each row's label says which kind
 * (up to date, no payments read, savings figures only).
 */
export const RUN_ROW_FILTERS = {
  ALL: {},
  UPDATED: { outcomes: ['APPLIED'] },
  UP_TO_DATE: { outcomes: ['SKIPPED'], matchState: 'MATCHED' },
  NOT_IN_RENOVA: { outcomes: ['SKIPPED'], matchState: 'UNMATCHED' },
  WAITING: { outcomes: ['NEEDS_REVIEW'], reviewStatuses: ['PENDING'] },
  FAILED: { outcomes: ['FAILED'] },
};

/**
 * What a payments read did with one policy, in a reader's terms.
 *
 * Returns `{ key, tone, label, hint, reasons, error, changes, changesLead,
 * savingsUpdated, paymentsNote }`: `label` and `hint` are i18n keys (hint null
 * when there is none), `reasons` the `heldReasons` of a row still waiting,
 * `error` the failure's own text, `changes` `heldChanges` pieces and
 * `changesLead` whether they are what accepting would do (a row still waiting)
 * rather than what was done. A combination nobody has named yet is `OTHER`,
 * which says only that the policy was read -- never a raw code or a crash.
 */
export function runRowResult(row) {
  const r = row || {};
  const d = (r.detail && typeof r.detail === 'object') ? r.detail : {};
  const base = {
    reasons: [],
    error: null,
    changes: [],
    changesLead: false,
    savingsUpdated: Boolean(d.savingsUpdated),
    paymentsNote: (typeof d.paymentsNote === 'string' && d.paymentsNote.trim()) || null,
    hint: null,
  };
  const result = (key, tone, extra = {}) => ({
    ...base,
    key,
    tone,
    label: `carriers.import.runRows.result.${key}`,
    ...extra,
  });
  const errorOf = () => {
    const e = d.applyError || d.error || d.parseError;
    return e ? String(e) : null;
  };

  if (r.outcome === 'APPLIED') return result('UPDATED', 'done', { changes: rowChanges(d) });
  if (r.outcome === 'FAILED') return result('FAILED', 'error', { error: errorOf() });
  if (r.reason === 'NO_MATCH') {
    return result('NOT_IN_RENOVA', 'muted', { hint: 'carriers.import.runRows.hint.NOT_IN_RENOVA' });
  }
  if (r.reason === 'AMBIGUOUS') {
    return result('AMBIGUOUS', 'warn', { hint: 'carriers.import.runRows.hint.AMBIGUOUS' });
  }
  if (r.outcome === 'NEEDS_REVIEW') {
    if (r.reviewStatus === 'ACCEPTED') return result('ACCEPTED', 'done', { changes: rowChanges(d) });
    if (r.reviewStatus === 'DISMISSED') return result('DISMISSED', 'muted');
    if (r.reviewStatus === 'PENDING') {
      return result('WAITING', 'warn', {
        reasons: heldReasons(d),
        error: d.applyError ? null : errorOf(),
        changes: rowChanges(d),
        changesLead: true,
      });
    }
    return result('OTHER', 'muted');
  }
  if (r.outcome === 'SKIPPED') {
    if (d.receiptsMissing) {
      // The worker's own words on why, when it has them; otherwise the general one.
      return result('NO_PAYMENTS', 'warn', { hint: base.paymentsNote ? null : 'carriers.import.runRows.hint.NO_PAYMENTS' });
    }
    if (d.noSchedule) return result('SAVINGS_ONLY', 'muted', { hint: 'carriers.import.runRows.hint.SAVINGS_ONLY' });
    if (r.reason === 'UP_TO_DATE' || r.reason === 'ALREADY_PAID_SAME_EVIDENCE') return result('UP_TO_DATE', 'ok');
  }
  return result('OTHER', 'muted');
}

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/**
 * A review section of a carrier read as a CSV, one line per receipt the carrier listed.
 *
 * `rows` are the staged rows as the list endpoint returns them; `why` is the sentence the
 * screen prints beside each (row -> string). A row whose report kept no receipts (Allianz's
 * one line per policy) still gets its line, with the policy and the reason.
 */
export function reviewRowsCsv(rows, why = () => '') {
  const header = ['Policy', 'Holder', 'Reason', 'Receipt', 'Endorsement',
    'Period start', 'Period end', 'Status', 'Amount', 'Paid on'];
  const lines = [header];
  (rows || []).forEach((row) => {
    const d = (row.detail && typeof row.detail === 'object') ? row.detail : {};
    const lead = [row.policyNumber || d.poliza || '', row.clientName || d.contratante || '', why(row) || ''];
    const receipts = Array.isArray(d.receipts) ? d.receipts : [];
    if (!receipts.length) {
      lines.push([...lead, '', '', '', '', d.derivedStatus || '', d.montoProximoPago || '', d.ultimaFechaPago || '']);
      return;
    }
    receipts.forEach((r) => {
      lines.push([...lead, r.receipt, r.endorsement, r.periodStart, r.periodEnd, r.status, r.amount, r.paidOn]);
    });
  });
  return `${lines.map((l) => l.map(csvCell).join(',')).join('\r\n')}\r\n`;
}
